import * as React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { useId } from '../../hooks/useId';
import positions, {
  eventNameMatcher,
  isCursorOutsideTooltipBoundaries,
} from './helpers';
import {
  Size,
  Theme,
  PointerPositions,
  TargetElement,
  BASE_CLASS_NAME,
} from './types';
import { useIsActive } from '../../hooks/useIsActive';
import { useCallback } from 'react';

type TooltipListenerProps = {
  [key: string]: (e: React.MouseEvent<HTMLElement>) => void;
};

type SupportedEventNames = 'focus' | 'click' | 'mouseenter';

export interface TooltipProps {
  content: string | React.ReactNode;
  className?: string;
  size: Size;
  theme: Theme;
  pointer?: boolean;
  pointerPosition?: PointerPositions;
  positionShift?: number;
  children: React.ReactNode;
  id?: string;
  tabIndex?: number;
  tooltipTriggerTestId?: string;
  tooltipContentTestId?: string;
  trigger?: string;
  isDefaultTriggerPadding?: boolean;
  borderRadius?: number;
  hideTimeout?: number;
  disablePortal?: boolean;
  showTimeout?: number;
  isInteractive?: boolean;
  preventTriggerEvent?: boolean;
  debounceMs?: number;
}

function Tooltip(props: TooltipProps) {
  const {
    id: idProp,
    className,
    size,
    theme,
    content,
    pointer = true,
    pointerPosition = 'bottom-right',
    children,
    tabIndex,
    tooltipTriggerTestId,
    tooltipContentTestId,
    trigger = 'mouseenter focus',
    hideTimeout = 1,
    disablePortal = false,
    showTimeout = 0,
    positionShift,
    isDefaultTriggerPadding = true,
    borderRadius,
    isInteractive = true,
    preventTriggerEvent = true,
    debounceMs = 100,
  } = props;
  const id = useId(idProp);
  const target = React.useRef<TargetElement>(null);
  const [tooltip, setTooltipRef] = React.useState<HTMLDivElement | null>(null);

  const {
    isActive: open,
    deactivate: handleClose,
    activate,
    isActivationPending,
  } = useIsActive({
    initVal: false,
    activateDelay: showTimeout,
    deactivateDelay: hideTimeout,
  });

  const handleShow = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (preventTriggerEvent) {
        e.preventDefault();
        e.stopPropagation();
      }
      activate();
    },
    [preventTriggerEvent]
  );

  const [placement, setPlacement] = React.useState<PointerPositions>(
    pointerPosition
  );

  const [styles, setStyles] = React.useState<Partial<React.CSSProperties>>({});

  React.useEffect(() => {
    if (borderRadius !== undefined) {
      setStyles({ ...styles, borderRadius });
    }
  }, [borderRadius]);

  const handleClickOutside = React.useCallback(
    (event) => {
      if (
        (target && target.current && target.current.contains(event.target)) ||
        (tooltip && tooltip.contains(event.target))
      ) {
        return;
      }
      handleClose();
    },
    [tooltip, target, handleClose]
  );
  const calculateStyle = React.useCallback(() => {
    if (tooltip && target.current) {
      const { styles: tooltipStyles, placement: tooltipPlacement } = positions(
        tooltip,
        target.current,
        pointerPosition,
        {
          hasArrow: pointer,
          size,
          shift: positionShift,
        }
      );

      setStyles({ ...styles, ...tooltipStyles });
      setPlacement(tooltipPlacement as PointerPositions);
    }
  }, [tooltip, target, pointerPosition, pointer, size]);
  const onMouseMove = React.useCallback(
    (event: MouseEvent) => {
      const eventTarget = event.target;
      const isCursorOverTrigger =
        target &&
        target.current &&
        // @ts-ignore
        target.current.contains(eventTarget);
      // @ts-ignore
      const isCursorOverTooltip = tooltip && tooltip.contains(eventTarget);

      if (!isInteractive && !isCursorOverTrigger) {
        return handleClose();
      }

      if (isCursorOverTrigger || isCursorOverTooltip) {
        return;
      }

      const shouldCloseTooltip = isCursorOutsideTooltipBoundaries({
        event,
        target: target.current,
        size,
        position: placement,
      });

      if (shouldCloseTooltip) {
        handleClose();
      }
    },
    [tooltip, target, handleClose, placement, size]
  );
  const debouncedOnMouseMove = debounce(onMouseMove, debounceMs);
  const tooltipListener: TooltipListenerProps = {};

  trigger
    .split(/\s+/)
    .filter(Boolean)
    .forEach((eventType: SupportedEventNames) => {
      tooltipListener[`${eventNameMatcher[eventType]}`] = handleShow;

      if (eventType === 'focus') {
        tooltipListener.onBlur = handleClose;
      }
    });

  React.useLayoutEffect(() => {
    const handleResize = debounce(calculateStyle, 100);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateStyle]);

  React.useEffect(() => {
    calculateStyle();
  }, [calculateStyle, tooltip, pointerPosition, pointer, size]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        handleClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClose, open]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    if (trigger.includes('click')) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (trigger.includes('click')) {
        document.removeEventListener('mousedown', handleClickOutside);
      }
    };
  }, [open, trigger, handleClickOutside]);

  React.useEffect(() => {
    if (!open && !isActivationPending) {
      return;
    }

    if (trigger.includes('mouseenter')) {
      document.addEventListener('mousemove', debouncedOnMouseMove);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (trigger.includes('mouseenter')) {
        document.removeEventListener('mousemove', debouncedOnMouseMove);
      }
    };
  }, [open, trigger, debouncedOnMouseMove, isActivationPending]);

  const tooltipClasses = classNames(
    className,
    BASE_CLASS_NAME,
    `${BASE_CLASS_NAME}--${size}`,
    `${BASE_CLASS_NAME}--${theme}`,
    {
      [`${BASE_CLASS_NAME}--open`]: open,
      [`${BASE_CLASS_NAME}--no-interaction`]: !isInteractive,
    }
  );
  const renderTooltip = () => (
    <div
      id={id}
      ref={setTooltipRef}
      role="tooltip"
      className={tooltipClasses}
      data-tooltip-placement={placement}
      style={styles}
      data-test-id={tooltipContentTestId}
    >
      {pointer && <div className={`${BASE_CLASS_NAME}-arrow`} />}
      <div className={`${BASE_CLASS_NAME}-inner`}>{content}</div>
    </div>
  );

  return (
    <>
      <div
        ref={target}
        className={classNames(`${BASE_CLASS_NAME}-trigger`, {
          [`${BASE_CLASS_NAME}-trigger--default-padding`]: isDefaultTriggerPadding,
        })}
        aria-describedby={id}
        tabIndex={tabIndex}
        data-test-id={tooltipTriggerTestId}
        {...tooltipListener}
      >
        {children}
      </div>
      {open &&
        (disablePortal
          ? renderTooltip()
          : ReactDOM.createPortal(renderTooltip(), document.body))}
    </>
  );
}

export default Tooltip;
